import type { EdgeId, NodeId } from '@ipfabric/utils';
import type { PacketDetail } from 'components/DeviceView/deviceViewTypes';
import { isPathLookupNodeData } from 'components/Graph/helpers/isPathLookupNodeData';
import type { GraphData } from 'components/Graph/model/GraphData';
import { getFollowingPacketIds } from './getFollowingPacketIds';
import { getGeneratedPacketDetail } from './getGeneratedPacketDetail';
import { getPacketEdgeDetail } from './getPacketEdgeDetail';
import { getTerminatedPacketDetail } from './getTerminatedPacketDetail';

/**
 * Returns forwarded or terminated packet.
 * Packet is searched in edge, in accepted packets and dropped packets in node in this order.
 * First found match is returned.
 *
 * Function must be used only if edge has 1 packet available.
 * There is no reason to select first found packet when user can choose from multiple packets.
 */
export const autoselectPacketDetail = (
  graphData: GraphData,
  firstPacketId: EdgeId,
  nodeId: NodeId | null,
  selectedOutgoingPacketId?: EdgeId,
): PacketDetail | null => {
  const followingPacketIds = getFollowingPacketIds(
    firstPacketId,
    graphData.edges[firstPacketId],
    nodeId !== null ? graphData.nodes[nodeId] : undefined,
  );
  const secondPacketId = selectedOutgoingPacketId ?? followingPacketIds?.[0];
  if (secondPacketId === undefined || nodeId === null) {
    return null;
  }

  if (graphData.edges[secondPacketId] !== undefined) {
    return getPacketEdgeDetail(graphData, secondPacketId);
  }

  const nodeData = graphData.nodes[nodeId];
  if (nodeData !== undefined && isPathLookupNodeData(nodeData)) {
    if (nodeData.acceptedPackets?.[secondPacketId] !== undefined) {
      return getTerminatedPacketDetail(
        secondPacketId,
        // Fixed noUncheckedIndexedAccess NIM-19408
        nodeData.acceptedPackets[secondPacketId]!,
      );
    }
    if (nodeData.droppedPackets?.[secondPacketId] !== undefined) {
      return getTerminatedPacketDetail(
        secondPacketId,
        // Fixed noUncheckedIndexedAccess NIM-19408
        nodeData.droppedPackets[secondPacketId]!,
      );
    }
    if (nodeData.generatedPackets?.[secondPacketId] !== undefined) {
      return getGeneratedPacketDetail(
        secondPacketId,
        // Fixed noUncheckedIndexedAccess NIM-19408
        nodeData.generatedPackets[secondPacketId]!,
      );
    }
  }

  return null;
};
